import type { ReactNode } from 'react';
import type { Action, Module, Role } from '../types/permissions';
import { TUTORIAL_MODULES } from '../content/tutorial';
import {
  AnimalCow,
  Bell,
  Briefcase,
  Calendar,
  ChartBar,
  ChartDown,
  ChartUp,
  Clipboard,
  FieldCrop,
  FileText,
  Home,
  Landscape,
  Lightbulb,
  Map,
  Money,
  Package,
  Pregnant,
  Refresh,
  Scale,
  Settings,
  Wrench,
  Health,
  CloudRain,
} from '../components/icons/IconLibrary';

/**
 * Catálogo del buscador global (command palette ⌘K).
 *
 * Fuente única de navegación del palette: incluye todas las páginas y
 * sub-páginas autenticadas de la app + los tabs deep-linkeables de
 * Herramientas (`/herramientas?app=<tab>`). Cada entrada declara su permiso
 * (módulo/acción o roles) y un set de `keywords` (sinónimos y formas
 * verbales en español) para que el buscador entienda "ingresar gasto",
 * "agregar nota", "calcular engorde", etc.
 *
 * NOTA: el menú lateral (`menuConfig.tsx`) queda intacto; este catálogo es
 * independiente y solo lo consume `CommandPalette`.
 */

export type SearchSection =
  | 'principal'
  | 'ganado'
  | 'campo'
  | 'finanzas'
  | 'reportes'
  | 'herramientas'
  | 'sistema'
  | 'tutoriales';

/** Orden de render de las secciones dentro del palette. */
export const SEARCH_SECTION_ORDER: SearchSection[] = [
  'principal',
  'ganado',
  'campo',
  'finanzas',
  'reportes',
  'herramientas',
  'sistema',
  'tutoriales',
];

export const SEARCH_SECTION_LABELS: Record<SearchSection, string> = {
  principal: 'Principal',
  ganado: 'Ganado',
  campo: 'Campo',
  finanzas: 'Finanzas',
  reportes: 'Reportes',
  herramientas: 'Herramientas',
  sistema: 'Sistema',
  tutoriales: 'Tutoriales',
};

export interface SearchEntry {
  id: string;
  label: string;
  /** Ruta base, ej: '/administracion/gastos' o '/herramientas'. */
  path: string;
  /** Query params a appendar (ej: { app: 'notas' } → ?app=notas). */
  searchParams?: Record<string, string>;
  section: SearchSection;
  /** Permiso de módulo; sin `module` la entrada es visible para todo autenticado. */
  module?: Module;
  /** Acción requerida sobre el módulo. Default: 'view'. */
  action?: Action;
  /** Restricción por rol de empresa (ej: Configuraciones, Usuarios). */
  roles?: Role[];
  icon: ReactNode;
  /** Alias / sinónimos para el match del buscador. */
  keywords?: string[];
}

// Iconos a tamaño uniforme para el palette.
const ic = {
  home: <Home size={20} />,
  dashboard: <ChartBar size={20} />,
  cow: <AnimalCow size={20} />,
  add: <Refresh size={20} />,
  historical: <Clipboard size={20} />,
  sessions: <Package size={20} />,
  lotes: <FieldCrop size={20} />,
  campos: <Landscape size={20} />,
  admin: <Money size={20} />,
  gastos: <ChartDown size={20} />,
  ingresos: <ChartUp size={20} />,
  stocks: <Package size={20} />,
  libro: <Clipboard size={20} />,
  resultados: <ChartBar size={20} />,
  compraVenta: <Refresh size={20} />,
  reporte: <FileText size={20} />,
  distribucion: <Map size={20} />,
  prenez: <Pregnant size={20} />,
  sanidad: <Health size={20} />,
  productividad: <ChartUp size={20} />,
  evolucion: <Scale size={20} />,
  herramientas: <Wrench size={20} />,
  agenda: <Calendar size={20} />,
  notas: <FileText size={20} />,
  recordatorios: <Bell size={20} />,
  lluvias: <CloudRain size={20} />,
  mapa: <Map size={20} />,
  calcEngorde: <Lightbulb size={20} />,
  config: <Settings size={20} />,
  usuarios: <Briefcase size={20} />,
};

/**
 * Keywords de un módulo del tutorial: palabras distintivas del título, la
 * descripción y los textos de los pasos (sin stopwords, sin acentos) — así
 * "cuanto vale mi ganado" encuentra el módulo de Valuación aunque no esté
 * en el título.
 */
const TUTORIAL_STOPWORDS = new Set([
  'como', 'para', 'donde', 'esta', 'este', 'todo', 'toda', 'todos', 'tus',
  'del', 'las', 'los', 'una', 'uno', 'que', 'con', 'por', 'mas', 'sin',
  'sus', 'cuando', 'hace', 'puede', 'podes', 'podés', 'tene', 'también',
  'tambien', 'sobre', 'entre', 'desde', 'hasta', 'cada', 'mismo', 'solo',
  'arriba', 'abajo', 'está', 'están',
]);
const keywordsDeTutorial = (m: typeof TUTORIAL_MODULES[number]): string[] => {
  const texto = `${m.title} ${m.description} ${m.steps.map(paso => paso.text).join(' ')}`;
  const palabras = texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .split(/[^a-z0-9]+/)
    .filter(w => w.length >= 4 && !TUTORIAL_STOPWORDS.has(w));
  return [...new Set(palabras)].slice(0, 12);
};

export const searchEntries: SearchEntry[] = [
  // --- Principal ---
  { id: 'home', label: 'Inicio', path: '/', section: 'principal', icon: ic.home },
  { id: 'dashboard', label: 'Panel principal', path: '/dashboard', section: 'principal', icon: ic.dashboard },
  // Foro de la comunidad: página pública (sin module — no gatea permisos).
  {
    id: 'foro',
    label: 'Foro',
    path: '/foro',
    section: 'principal',
    icon: ic.dashboard,
    keywords: ['foro', 'comunidad', 'pregunta', 'preguntas', 'publicar', 'tema', 'discusion', 'discusión', 'foro comunidad'],
  },
  // Mapa del Campo y Lluvias: páginas propias del menú Principal.
  {
    id: 'mapa-campo',
    label: 'Mapa del Campo',
    path: '/mapa',
    section: 'principal',
    module: 'tools',
    icon: ic.mapa,
    keywords: [
      'mapa',
      'satelite',
      'satélite',
      'ndvi',
      'vegetacion',
      'vegetación',
      'fotosintesis',
      'fotosíntesis',
      'cuadro',
      'cuadros',
      'corral',
      'corrales',
      'aguada',
      'aguadas',
      'hectareas',
      'hectáreas',
      'relieve',
      'medir',
      'puesto',
      'viaje atras',
      'viaje atrás',
      'wayback',
    ],
  },
  {
    id: 'lluvias',
    label: 'Lluvias',
    path: '/lluvias',
    section: 'principal',
    module: 'tools',
    icon: ic.lluvias,
    keywords: [
      'lluvia',
      'lluvias',
      'registrar lluvia',
      'milimetros',
      'milímetros',
      'mm',
      'precipitaciones',
      'tormenta',
      'agua caida',
      'agua caída',
    ],
  },

  // --- Ganado ---
  {
    id: 'animals',
    label: 'Mi Ganado',
    path: '/animals',
    section: 'ganado',
    module: 'animals',
    icon: ic.cow,
    keywords: ['ganado', 'vaca', 'vacas', 'animal', 'animales', 'rodeo', 'hacienda', 'ver ganado'],
  },
  {
    id: 'animals-new',
    label: 'Nueva Sesión',
    path: '/animals/new',
    section: 'ganado',
    module: 'animals',
    action: 'create',
    icon: ic.add,
    keywords: [
      'nueva sesion',
      'nueva sesión',
      'nueva vaca',
      'nuevo animal',
      'agregar vaca',
      'agregar animal',
      'cargar animal',
      'cargar vaca',
      'alta animal',
      'alta vaca',
      'caravanear',
      'registrar animal',
    ],
  },
  {
    id: 'animals-historical',
    label: 'Histórico de animales',
    path: '/animals/historical',
    section: 'ganado',
    module: 'animals_history',
    icon: ic.historical,
    keywords: ['historico', 'histórico', 'historial', 'archivo', 'movimientos', 'bajas'],
  },
  {
    id: 'sesiones',
    label: 'Sesiones de Carga',
    path: '/sesiones',
    section: 'ganado',
    module: 'animals',
    icon: ic.sessions,
    keywords: ['sesion', 'sesiones', 'carga', 'cargas', 'session', 'lote de carga'],
  },
  {
    id: 'declaracion-sigsa',
    label: 'Declaración SIGSA',
    path: '/declaracion-sigsa',
    section: 'ganado',
    module: 'sigsa',
    icon: ic.reporte,
    keywords: ['sigsa', 'senasa', 'declaracion', 'declaración', 'declarar', 'caravanas', 'txt', 'trazabilidad'],
  },

  // --- Campo ---
  {
    id: 'ubicaciones',
    label: 'Lotes y Corrales',
    path: '/ubicaciones',
    section: 'campo',
    module: 'locations',
    icon: ic.lotes,
    keywords: ['lote', 'lotes', 'corral', 'corrales', 'potrero', 'potreros', 'ubicacion', 'ubicación', 'campo'],
  },
  {
    id: 'campo-stock-analysis',
    label: 'Movimientos de Hacienda',
    path: '/campo/stock-analysis',
    section: 'campo',
    module: 'locations',
    icon: ic.resultados,
    keywords: ['hacienda', 'movimientos', 'traslados', 'stock', 'entradas', 'salidas', 'ganado'],
  },
  {
    id: 'ubicaciones-new',
    label: 'Nuevo Corral',
    path: '/ubicaciones/new',
    section: 'campo',
    module: 'locations',
    action: 'create',
    icon: ic.add,
    keywords: [
      'nuevo corral',
      'nuevo lote',
      'agregar corral',
      'agregar lote',
      'crear corral',
      'crear lote',
      'alta corral',
    ],
  },
  {
    id: 'campos',
    label: 'Campos',
    path: '/campos',
    section: 'campo',
    module: 'locations',
    icon: ic.campos,
    keywords: ['campo', 'campos', 'establecimiento', 'unidad', 'fondo'],
  },
  {
    id: 'campos-new',
    label: 'Nuevo Campo',
    path: '/campos/new',
    section: 'campo',
    module: 'locations',
    action: 'create',
    icon: ic.add,
    keywords: ['nuevo campo', 'agregar campo', 'crear campo', 'alta campo', 'registrar campo'],
  },

  // --- Finanzas ---
  {
    id: 'administracion',
    label: 'Administración',
    path: '/administracion',
    section: 'finanzas',
    module: 'financial',
    icon: ic.admin,
    keywords: ['administracion', 'administración', 'finanzas', 'economia', 'economía', 'contable'],
  },
  {
    id: 'gastos',
    label: 'Gastos',
    path: '/administracion/gastos',
    section: 'finanzas',
    module: 'financial',
    icon: ic.gastos,
    keywords: ['gasto', 'gastos', 'ingresar gasto', 'cargar gasto', 'nuevo gasto', 'egreso', 'egresos', 'gastar'],
  },
  {
    id: 'ingresos',
    label: 'Ingresos',
    path: '/administracion/ingresos',
    section: 'finanzas',
    module: 'financial',
    icon: ic.ingresos,
    keywords: ['ingreso', 'ingresos', 'nuevo ingreso', 'cargar ingreso', 'ganancia', 'venta ingreso'],
  },
  {
    id: 'stocks',
    label: 'Inventario',
    path: '/administracion/stocks',
    section: 'finanzas',
    module: 'financial',
    icon: ic.stocks,
    keywords: ['stock', 'stocks', 'inventario', 'existencias', 'mercaderia', 'mercadería'],
  },
  {
    id: 'libro-diario',
    label: 'Libro Diario',
    path: '/administracion/libro-diario',
    section: 'finanzas',
    module: 'financial',
    icon: ic.libro,
    keywords: ['libro', 'libro diario', 'asiento', 'asientos', 'contabilidad', 'diario'],
  },
  {
    id: 'resultados',
    label: 'Resultados',
    path: '/administracion/resultados',
    section: 'finanzas',
    module: 'financial',
    icon: ic.resultados,
    keywords: ['resultado', 'resultados', 'balance', 'margen', 'ganancia perdida', 'cierre', 'estado resultado'],
  },
  {
    id: 'operaciones-compra-venta',
    label: 'Compra y Venta',
    path: '/operaciones-compra-venta',
    section: 'finanzas',
    module: 'financial',
    icon: ic.compraVenta,
    keywords: [
      'compra',
      'venta',
      'compraventa',
      'compra venta',
      'comprar',
      'vender',
      'operacion',
      'operación',
      'operaciones',
      'transaccion',
      'transacción',
    ],
  },

  // --- Reportes ---
  {
    id: 'reports',
    label: 'Reporte del Campo',
    path: '/reports',
    section: 'reportes',
    module: 'reports',
    icon: ic.reporte,
    keywords: ['reporte', 'reportes', 'informe', 'informes', 'estadistica', 'estadística', 'reporte del campo'],
  },
  {
    id: 'reports-distribution',
    label: 'Distribución del Ganado',
    path: '/reports/distribution',
    section: 'reportes',
    module: 'reports',
    icon: ic.distribucion,
    keywords: ['distribucion', 'distribución', 'reparto', 'ubicacion ganado', 'donde estan', 'mapa ganado'],
  },
  {
    id: 'reports-pregnancy',
    label: 'Análisis de Preñez',
    path: '/reports/pregnancy',
    section: 'reportes',
    module: 'reports',
    icon: ic.prenez,
    keywords: ['preñez', 'prenez', 'preñar', 'reproduccion', 'reproducción', 'tacto', 'paricion', 'parición', 'gestacion'],
  },
  {
    id: 'reports-health',
    label: 'Análisis de Sanidad',
    path: '/reports/health',
    section: 'reportes',
    module: 'reports',
    icon: ic.sanidad,
    keywords: ['sanidad', 'salud', 'vacuna', 'vacunacion', 'vacunación', 'enfermedad', 'tratamiento', 'veterinaria', 'vet'],
  },
  {
    id: 'reports-productivity',
    label: 'Análisis de Productividad',
    path: '/reports/productivity',
    section: 'reportes',
    module: 'reports',
    icon: ic.productividad,
    keywords: ['productividad', 'produccion', 'producción', 'eficiencia', 'rendimiento'],
  },
  {
    id: 'reports-weight-evolution',
    label: 'Evolución del Peso',
    path: '/reports/weight-evolution',
    section: 'reportes',
    module: 'reports',
    icon: ic.evolucion,
    keywords: ['peso', 'evolucion peso', 'evolución del peso', 'ganancia diaria', 'gdp', 'curva de peso'],
  },

  // --- Herramientas (tabs deep-linkeables) ---
  {
    id: 'herramientas',
    label: 'Herramientas',
    path: '/herramientas',
    section: 'herramientas',
    module: 'tools',
    icon: ic.herramientas,
    keywords: ['herramienta', 'herramientas', 'utilidades', 'util'],
  },
  {
    id: 'agenda',
    label: 'Agenda',
    path: '/herramientas',
    searchParams: { app: 'agenda' },
    section: 'herramientas',
    module: 'tools',
    icon: ic.agenda,
    keywords: ['agenda', 'calendario', 'evento', 'eventos', 'cita', 'turno'],
  },
  {
    id: 'notas',
    label: 'Notas',
    path: '/herramientas',
    searchParams: { app: 'notas' },
    section: 'herramientas',
    module: 'tools',
    icon: ic.notas,
    keywords: ['nota', 'notas', 'agregar nota', 'nueva nota', 'anotacion', 'anotación', 'anotaciones', 'recordar nota'],
  },
  {
    id: 'recordatorios',
    label: 'Recordatorios',
    path: '/herramientas',
    searchParams: { app: 'recordatorios' },
    section: 'herramientas',
    module: 'tools',
    icon: ic.recordatorios,
    keywords: [
      'recordatorio',
      'recordatorios',
      'agregar recordatorio',
      'nuevo recordatorio',
      'aviso',
      'avisos',
      'alarma',
      'alarmas',
      'recordar',
      'alerta',
    ],
  },
  {
    id: 'calculadora-engorde',
    label: 'Calculadora de Engorde',
    path: '/herramientas',
    searchParams: { app: 'calculadora-engorde' },
    section: 'herramientas',
    module: 'tools',
    icon: ic.calcEngorde,
    keywords: [
      'calculadora',
      'engorde',
      'calcular engorde',
      'calc engorde',
      'ganancia de peso',
      'ganancia diaria',
      'gdp',
      'costo engorde',
    ],
  },

  // --- Sistema (por rol de empresa) ---
  {
    id: 'configuraciones',
    label: 'Configuraciones',
    path: '/configuraciones',
    section: 'sistema',
    roles: ['owner', 'admin'],
    icon: ic.config,
    keywords: ['configuracion', 'configuración', 'configuraciones', 'ajustes', 'preferencias', 'setup'],
  },
  {
    id: 'usuarios',
    label: 'Usuarios',
    path: '/admin/usuarios',
    section: 'sistema',
    roles: ['owner', 'admin'],
    icon: ic.usuarios,
    keywords: ['usuario', 'usuarios', 'miembro', 'miembros', 'equipo', 'permisos', 'invitacion', 'invitación', 'invitar'],
  },
  // --- Tutoriales (KAN-232): autogenerados del contenido del tutorial ---
  ...TUTORIAL_MODULES.map((m): SearchEntry => ({
    id: `tutorial-${m.id}`,
    label: `Tutorial: ${m.title}`,
    path: '/tutorial',
    searchParams: { modulo: m.id },
    section: 'tutoriales' as SearchSection,
    module: m.permission.module as Module,
    action: m.permission.action as Action,
    icon: ic.calcEngorde,
    keywords: keywordsDeTutorial(m),
  })),
];

/**
 * Devuelve la URL final de una entrada: path + query string si tiene searchParams.
 */
export function buildPath(e: SearchEntry): string {
  if (!e.searchParams) return e.path;
  const qs = new URLSearchParams(e.searchParams).toString();
  return qs ? `${e.path}?${qs}` : e.path;
}

/**
 * Normaliza un texto para comparación diacrítica/insensible a mayúsculas:
 * trim + lower + quitar tildes y diacríticos combinables.
 */
export function normalizeSearch(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * Stopwords que se ignoran al tokenizar (no aportan para el match).
 * Ej: "evolución del peso" ≈ "evolucion peso".
 */
const STOPWORDS = new Set([
  'de', 'del', 'la', 'el', 'las', 'los', 'y', 'o', 'en', 'para',
  'un', 'una', 'unos', 'unas', 'mi', 'al', 'lo', 'me', 'que',
]);

/**
 * Mapeo de verbos/sustantivos de acción a un canónico, para que variantes
 * naturales del usuario ("agregar", "cargar", "crear", "ingresar", "nuevo",
 * "registrar", "alta", "añadir") matcheen lo mismo aunque no estén como
 * keyword literal. También unifica verbos de visualización.
 */
const VERB_MAP: Record<string, string> = {
  // creación / alta
  agregar: 'crear', cargar: 'crear', crear: 'crear', ingresar: 'crear',
  registrar: 'crear', alta: 'crear', nuevo: 'crear', nueva: 'crear',
  nuevos: 'crear', nuevas: 'crear', anadir: 'crear', añadir: 'crear',
  poner: 'crear', hacer: 'crear', generar: 'crear', dar: 'crear',
  // visualización / navegación
  ver: 'ver', mostrar: 'ver', abrir: 'ver', ir: 'ver', listar: 'ver',
  consultar: 'ver', encontrar: 'ver', buscar: 'ver', mirar: 'ver',
};

/**
 * Stemming muy liviano (plural → singular) para que "gastos"≈"gasto",
 * "notas"≈"nota", "animales"≈"animal". No es un stemmer completo, alcanza
 * para cubrir los casos comunes del catálogo.
 */
function stem(t: string): string {
  if (t.length > 4 && t.endsWith('es')) return t.slice(0, -2);
  if (t.length > 3 && t.endsWith('s')) return t.slice(0, -1);
  return t;
}

/**
 * Tokeniza un texto para el match: normaliza → split → mapeo verbal →
 * stem → quita stopwords. El orden de las palabras no importa.
 */
function toTokens(s: string): string[] {
  return normalizeSearch(s)
    .split(/\s+/)
    .filter(Boolean)
    .map((t) => VERB_MAP[t] ?? t)
    .map(stem)
    .filter((t) => !STOPWORDS.has(t));
}

/**
 * Score de match entre query y entrada. Mayor = mejor. 0 = no match.
 *
 * Estrategia tolerante a variaciones:
 *   - Coincidencia exacta de frase con el label → 10
 *   - Label empieza con el query → 8
 *   - Todos los tokens del query (verbos normalizados + stem) están en la
 *     entrada (label ∪ keywords), en cualquier orden → 6
 *   - Match parcial de tokens (≥50%) → 2..5
 *   - Fallback por substring del label / keywords (typos o prefijos) → 1..2
 */
export function matchScore(e: SearchEntry, qNormalized: string): number {
  if (!qNormalized) return 0;
  const label = normalizeSearch(e.label);
  if (label === qNormalized) return 10;
  if (label.startsWith(qNormalized)) return 8;

  const qTokens = toTokens(qNormalized);

  // Si el query eran solo stopwords/verbos, caer a substring simple.
  if (qTokens.length === 0) {
    return label.includes(qNormalized) ? 2 : 0;
  }

  const entryTokens = new Set<string>([
    ...toTokens(e.label),
    ...(e.keywords ?? []).flatMap(toTokens),
  ]);

  const matched = qTokens.filter((t) => entryTokens.has(t));
  const ratio = matched.length / qTokens.length;
  if (ratio === 1) return 6;
  if (ratio >= 0.5) return Math.max(2, Math.round(ratio * 5));

  // Fallback: substring parcial sobre el label o algún keyword.
  if (label.includes(qNormalized)) return 2;
  if ((e.keywords ?? []).some((k) => normalizeSearch(k).includes(qNormalized))) return 1;
  return 0;
}

const SECTION_RANK: Record<SearchSection, number> = SEARCH_SECTION_ORDER.reduce(
  (acc, s, i) => {
    acc[s] = i;
    return acc;
  },
  {} as Record<SearchSection, number>,
);

/**
 * Filtra el catálogo por query y permisos del usuario en la empresa activa.
 * Resultado agrupable por section: ordenado por section (SEARCH_SECTION_ORDER)
 * y, dentro de cada section, por mejor score descendente.
 *
 * Puramente testeable: recibe `can` y `hasRole` por parámetro (sin depender
 * de hooks/React), así no arrastra la cadena de AuthContext en los tests.
 * `CommandPalette` obtiene esas funciones de `usePermissions` y se las pasa.
 */
export function filterAndSortEntries(
  entries: SearchEntry[],
  query: string,
  can: (m: Module, a: Action) => boolean,
  hasRole: (r: Role[]) => boolean,
): SearchEntry[] {
  const visible = entries.filter((e) => {
    if (e.roles && e.roles.length > 0 && !hasRole(e.roles)) return false;
    if (e.module && !can(e.module, e.action ?? 'view')) return false;
    return true;
  });

  const q = normalizeSearch(query);
  if (!q) return visible;

  return visible
    .map((e) => ({ e, score: matchScore(e, q) }))
    .filter((x) => x.score > 0)
    .sort((a, b) => {
      const sa = SECTION_RANK[a.e.section];
      const sb = SECTION_RANK[b.e.section];
      if (sa !== sb) return sa - sb;
      return b.score - a.score; // mejor match primero dentro de la sección
    })
    .map((x) => x.e);
}
